import React, { useState } from 'react';
import { Edit2, FileText, Plus, Send } from 'lucide-react';
import { useEmailTemplates, sendTestEmail } from '../../../hooks/useEmailAdmin';
import TemplateEditor from './TemplateEditor';
import TemplatePreview from './TemplatePreview';

export default function TemplateManagement() {
  const { templates, loading, error, updateTemplate, createTemplate } = useEmailTemplates();
  const [selectedTemplate, setSelectedTemplate] = useState<any | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [previewMode, setPreviewMode] = useState<'html' | 'text'>('html');
  const [testEmail, setTestEmail] = useState('');
  const [testStatus, setTestStatus] = useState<{ type?: 'success' | 'error'; message?: string }>({});

  const templateTypes = [
    { id: 'daily_digest', name: 'Daily Digest' },
    { id: 'weekly_digest', name: 'Weekly Digest' },
    { id: 'instant_alert', name: 'Instant Alert' },
    { id: 'verification', name: 'Email Verification' },
  ];

  const handleSaveTemplate = async (template: any) => {
    try {
      await updateTemplate(template.id, template);
      setIsEditing(false);
      setTestStatus({ type: 'success', message: 'Template saved successfully' });
    } catch (err) {
      setTestStatus({ type: 'error', message: 'Failed to save template' });
    }
  };

  const handleSendTest = async () => {
    if (!testEmail || !selectedTemplate) return;
    
    setTestStatus({});
    try {
      await sendTestEmail(testEmail, selectedTemplate.templateType);
      setTestStatus({ type: 'success', message: 'Test email sent successfully' });
    } catch (err) {
      setTestStatus({ type: 'error', message: 'Failed to send test email' });
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading templates...</div>;
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 rounded-md">
        Error: {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Email Templates
        </h2>
        <button
          onClick={() => {
            setIsEditing(true);
            setSelectedTemplate({
              templateType: 'new_template',
              language: 'en',
              subject: '',
              htmlContent: '',
              textContent: '',
              availableVariables: [],
              isActive: true,
              version: '1.0.0',
            });
          }}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          <Plus size={16} />
          Create New Template
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Template List */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Available Templates</h3>
          {templateTypes.map((type) => (
            <div key={type.id} className="border rounded-lg p-4">
              <h4 className="font-medium mb-2">{type.name}</h4>
              <div className="space-y-2">
                {templates
                  .filter(t => t.templateType === type.id)
                  .map((template) => (
                    <div
                      key={template.id}
                      onClick={() => {
                        setSelectedTemplate(template);
                        setIsEditing(false);
                      }}
                      className={`p-3 rounded-md cursor-pointer transition-colors ${
                        selectedTemplate?.id === template.id
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 border'
                          : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">
                          {template.language.toUpperCase()} - v{template.version}
                        </span>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          template.isActive
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-200'
                        }`}>
                          {template.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        {template.subject}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>

        {/* Template Editor/Preview */}
        <div className="space-y-4">
          {selectedTemplate ? (
            <div className="border rounded-lg p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                  {isEditing ? 'Edit Template' : 'Template Details'}
                </h3>
                {!isEditing && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-2 bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700"
                  >
                    <Edit2 size={16} />
                    Edit
                  </button>
                )}
              </div>

              {isEditing ? (
                <TemplateEditor
                  template={selectedTemplate}
                  onSave={handleSaveTemplate}
                  onCancel={() => setIsEditing(false)}
                />
              ) : (
                <>
                  <TemplatePreview 
                    template={selectedTemplate}
                    mode={previewMode}
                    onModeChange={setPreviewMode}
                  />
                  
                  {/* Test Email Section */}
                  <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                    <h4 className="font-medium mb-3">Send Test Email</h4>
                    <div className="flex gap-2">
                      <input
                        type="email"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        placeholder="test@example.com"
                        className="flex-1 p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600"
                      />
                      <button
                        onClick={handleSendTest}
                        disabled={!testEmail}
                        className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Send size={16} />
                        Send Test
                      </button>
                    </div>
                    {testStatus.message && (
                      <div className={`mt-2 text-sm ${
                        testStatus.type === 'success' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {testStatus.message}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="border rounded-lg p-8 text-center text-gray-500 dark:text-gray-400">
              Select a template to view or edit
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
